"use client";

import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeroUIProvider } from "@heroui/react";
import { useRouter } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { AptosWalletAdapterProvider } from "@aptos-labs/wallet-adapter-react";
import { Network } from "@aptos-labs/ts-sdk";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/contexts/auth-context";
import { ConnectionProvider } from "@/contexts/connection-context";
import { AuthInitializer } from "@/components/AuthInitializer";
import { Toaster } from "sonner";

interface ProvidersProps {
  children: React.ReactNode;
  themeProps?: {
    attribute: string;
    defaultTheme: string;
  };
}

export function Providers({ children, themeProps }: ProvidersProps) {
  const router = useRouter();

  return (
    <AptosWalletAdapterProvider 
      autoConnect={false}
      dappConfig={{
        network: Network.MAINNET,
        aptosApiKeys: {
          mainnet: process.env.NEXT_PUBLIC_APTOS_API_KEY_MAINNET,
        },
        aptosConnectDappId: "rhuna-portal"
      }}
      onError={(error) => {
        console.log("Wallet adapter error:", error);
      }}
    >
      <AuthProvider>
        <ConnectionProvider>
          <AuthInitializer>
            <QueryClientProvider client={queryClient}>
              <HeroUIProvider navigate={router.push}>
                <NextThemesProvider attribute={(themeProps?.attribute as "class" | "data-theme" | undefined) || "class"} defaultTheme={themeProps?.defaultTheme || "light"}>
                  <Toaster />
                  {children}
                  <ReactQueryDevtools initialIsOpen={false} />
                </NextThemesProvider>
              </HeroUIProvider>
            </QueryClientProvider>
          </AuthInitializer>
        </ConnectionProvider>
      </AuthProvider>
    </AptosWalletAdapterProvider>
  );
}
